<!-- PointCloudView.vue -->
<script lang="ts">
import { defineComponent, computed, onMounted, ref, watch } from 'vue'
import type { ComputedRef, Ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import MapComponent from '@/components/MapComponent.vue'
import { useMapStore } from '@/stores/mapStore'
import VectorSource from 'ol/source/Vector.js'
import FileListVue from '@/components/FileList.vue'
import WebGLPointsLayer from 'ol/layer/WebGLPoints'
import Positioning from 'ol/Overlay'
import GeoJSON from 'ol/format/GeoJSON'
import WKT from 'ol/format/WKT'
import { Style, Circle as CircleStyle, Fill } from 'ol/style'
import Overlay from 'ol/Overlay'
import Chart from 'chart.js/auto'
import Collection from 'ol/Collection.js'
import Feature from 'ol/Feature.js'
import Point from 'ol/geom/Point.js'
import { fromLonLat, toLonLat } from 'ol/proj.js'
import { Polygon } from 'ol/geom.js'
import { Vector as VectorLayer } from 'ol/layer.js'
import { Stroke } from 'ol/style.js'

// vue date range
import { useDatasets } from '@/composables/useDatasets'
import type { Dataset } from '@/composables/useDatasets'
import { add } from 'ol/coordinate'

import { useSites } from '@/composables/useSites'

// info drawer
import { useDrawer } from '@/composables/useDrawer'

export default defineComponent({
    components: {
        MapComponent,
        FileListVue
    },

    setup() {
        const drawer = useDrawer()

        const route = useRoute()
        const router = useRouter()
        const siteId = computed(() => route.params.site_id) as ComputedRef<string>

        const goBackToSite = () => {
            if (siteId.value) {
                router.push(`/site/${siteId.value}/`)
            }
        }
        console.log('Site ID:', siteId.value)
        console.log('route params:', route.params)
        const mapStore = useMapStore()
        const datasetsAdapter = useDatasets(route.params.site_id as string)
        const sitesAdapter = useSites()
        const selectedDataset: Ref<Dataset | null> = ref(null)
        const slider = document.getElementById('coherence-threshold')
        const cohThreshold = ref(0.7)
        const velThreshold = ref(0.005)
        const baseWidth = ref(720)
        const pointSize = ref(2)
        const maxConcurrentRequests = ref(5)
        const selectedPoint = ref(null)
        const siteObject = ref(null)
        const isChartOverlayOpen = ref(false)
        const aoiLayerVisible = ref(true)
        let aoiLayer: VectorLayer<VectorSource> | null = null

        const categories = ref([
            { name: 'Structural Health Monitoring', key: 'S', route_key: 'shm', active: true },
            { name: 'Coastal Monitoring', key: 'C', route_key: 'coastal', active: false },
            {
                name: 'Environmental Monitoring',
                key: 'E',
                route_key: 'environmental',
                active: false
            },
            { name: 'Alerts and change detection', key: 'A', route_key: 'alerts', active: false }
        ])
        const selectedCategory = ref('S')
        
        // Track if controls have changed to show reload button
        const controlsChanged = ref(false)
        const initialControlValues = ref({
            cohThreshold: cohThreshold.value,
            velThreshold: velThreshold.value,
            pointSize: pointSize.value,
            maxConcurrentRequests: maxConcurrentRequests.value,
            selectedCategory: selectedCategory.value
        })
        
        const reloadWithNewControls = () => {
            if (!selectedDataset.value) {
                console.log('No dataset selected for reload')
                return
            }

            // Reload the current dataset with new control values
            findFeaturesForDataset()
            
            // Reset the controls changed flag and update initial values
            controlsChanged.value = false
            initialControlValues.value = {
                cohThreshold: cohThreshold.value,
                velThreshold: velThreshold.value,
                pointSize: pointSize.value,
                maxConcurrentRequests: maxConcurrentRequests.value,
                selectedCategory: selectedCategory.value
            }
        }

        const updatePopupWidth = (mapWidth: number) => {
            // Set the width of the popup as a proportion of the map width
            console.log('map width is', mapWidth)
            baseWidth.value = mapWidth * 0.6666
            console.log(
                'baseWidth.value (setting initial popup width for chart) set to:',
                baseWidth.value
            )
        }

        // Function to copy time series data to clipboard in column format
        const copyTimeSeriesData = (
            dates: string[],
            values: number[],
            lat: number,
            lon: number
        ) => {
            // Create header column
            const headers = ['lat', 'lon', ...dates]

            // Create values column
            const valuesColumn = [
                lat.toString(),
                lon.toString(),
                ...values.map((v) => v.toString())
            ]

            // Join each column with newlines
            const headerText = headers.join('\n')
            const valueText = valuesColumn.join('\n')

            // Create tab-separated text for easy pasting into Excel
            const clipboardText = headers
                .map((header, index) => `${header}\t${valuesColumn[index]}`)
                .join('\n')

            // Copy to clipboard
            navigator.clipboard
                .writeText(clipboardText)
                .then(() => {
                    console.log('Time series data copied to clipboard')
                    // You could add a toast notification here
                })
                .catch((err) => {
                    console.error('Failed to copy to clipboard:', err)
                })
        }

        const featuresContainer = ref(new Collection())
        const globalGeoJsonFormat = new GeoJSON()

        console.log(siteId.value)
        const handleDatasetChange = (dataset: Dataset) => {
            // search for the dataset in the datasets array
            // const dataset = datasetsAdapter.datasets.value.find(
            //   (dataset) => dataset.name === datasetName
            // ) as Dataset
            selectedDataset.value = dataset
            console.log(
                `Selected dataset: ${selectedDataset.value?.id} - ${selectedDataset.value?.name}`
            )
            // zoomToDataSet();
        }

        const set_preset = () => {
            if (selectedCategory.value === 'S') {
                cohThreshold.value = 0.8
                velThreshold.value = 0.005
            } else if (selectedCategory.value === 'C') {
                cohThreshold.value = 0.6
                velThreshold.value = 0.01
            } else if (selectedCategory.value === 'E') {
                cohThreshold.value = 0.4
                velThreshold.value = 0.01
            } else if (selectedCategory.value === 'A') {
                cohThreshold.value = 0.7
                velThreshold.value = 0.005
            }
            // set the active category
            categories.value.forEach((cat) => {
                cat.active = cat.key === selectedCategory.value
            })
        }

        // check route params for a 'psi_settings' key
        if (route.params.psi_settings) {
            console.log('Route params:', route.params.psi_settings)
            const psi_settings = route.params.psi_settings
            console.log('Parsed psi_settings:', psi_settings)
            // psi_settings might be 'shm', 'coastal', 'environmental', 'alerts'
            if (psi_settings) {
                const cat = categories.value.find((cat) => cat.route_key === psi_settings)
                if (cat) {
                    selectedCategory.value = cat.key
                    console.log('Selected category:', selectedCategory.value)
                    set_preset()
                }
            }
        }
        console.log('123')

        const pointsLayer: Ref<WebGLPointsLayer<VectorSource> | null> = ref(null)

        function hsvToRgb(h: number, s: number, v: number) {
            // Convert hue to a scale between 0 and 240 degrees (hue in degrees)
            h = h * 240 // Scale the input hue [0, 1] to [0, 240]

            let r = 0,
                g = 0,
                b = 0
            const i = Math.floor(h / 60) // Find the sector of the hue
            const f = h / 60 - i // Fractional part of the hue
            const p = v * (1 - s) // p = value * (1 - saturation)
            const q = v * (1 - f * s) // q = value * (1 - f * saturation)
            const t = v * (1 - (1 - f) * s) // t = value * (1 - (1 - f) * saturation)

            switch (i) {
                case 0:
                    ;(r = v), (g = t), (b = p)
                    break
                case 1:
                    ;(r = q), (g = v), (b = p)
                    break
                case 2:
                    ;(r = p), (g = v), (b = t)
                    break
                case 3:
                    ;(r = p), (g = q), (b = v)
                    break
                case 4:
                    ;(r = t), (g = p), (b = v)
                    break
                case 5:
                    ;(r = v), (g = p), (b = q)
                    break
            }

            // Convert RGB to [0, 255] range
            return [Math.round(b * 255), Math.round(g * 255), Math.round(r * 255)]
        }

        function linspace(start: number, end: number, numStops: number) {
            const step = (end - start) / (numStops - 1)
            const result = []

            for (let i = 0; i < numStops; i++) {
                result.push(start + i * step)
            }

            return result
        }
        function generateColorMap(threshold: number) {
            // Define color map base thresholds
            const thresholds = linspace(-threshold, threshold, 21)

            // Use HSV values for smoother gradients
            const colors = thresholds.map((value, index) => {
                // Generate a hue value that increases with each threshold
                const hue = index / (thresholds.length - 1) // from 0 to 1
                // const saturation = 1;  // full saturation
                // reduce saturation to 0.7 at half way
                const saturation = 0.5 + 0.5 * Math.abs(hue - 0.5) * 2

                const brightness = 1 // full brightness
                const [r, g, b] = hsvToRgb(hue, saturation, brightness) // Convert HSV to RGB
                return `rgb(${r}, ${g}, ${b})`
            })

            // Generate the color map using the thresholds and generated colors
            return [
                'interpolate',
                ['linear'],
                ['get', 'velocity'],
                ...thresholds.flatMap((value, index) => [value, colors[index]])
            ]
        }

        const buildPointsLayerStyle = () => ({
            'circle-radius': pointSize.value,
            'circle-fill-color': generateColorMap(velThreshold.value),
            'circle-rotate-with-view': false,
            'circle-displacement': [0, 0]
        })

        const layerSetup = () => {
            console.log('Setting up WebGLPointsLayer')
            if (!mapStore.map) {
                throw new Error('Map not initialized')
            }

            // Recreate the layer in place so style updates always apply and layers do not stack.
            if (pointsLayer.value) {
                mapStore.map.removeLayer(pointsLayer.value)
            }

            pointsLayer.value = new WebGLPointsLayer({
                source: new VectorSource(),
                style: buildPointsLayerStyle()
            })
            mapStore.map.addLayer(pointsLayer.value)
        }

        const pointsSource = () => {
            if (!pointsLayer.value) {
                throw new Error('Points layer not initialized')
            }
            const source = pointsLayer.value.getSource()
            if (!source) {
                // we need to create a new source
            }
            return <VectorSource>pointsLayer.value.getSource()
        }

        type ScattererPoint = [number | null, number | null, number | null, number | null, number | null]
        type FilteredScattererPoint = [number, number, number, number, number]

        function addPointsToMap(batchData: FilteredScattererPoint[]) {
            const features = batchData.map((point) => {
                // The order of the elements is: lon, lat, coherence, velocity, height_error
                const lon = point[1]
                const lat = point[0]
                const coherence = point[2]
                const velocity = point[3]
                const height_error = point[4]

                // Create a feature for each point
                const feature = new Feature({
                    geometry: new Point(fromLonLat([lon, lat])), // Convert coordinates to map projection
                    coherence,
                    velocity,
                    height_error
                })
                return feature
            })

            // Add features to the source
            const source = pointsSource()
            source.addFeatures(features)
        }
        const zoomToDataSet = () => {
            if (!mapStore.map) {
                console.log('No map')
                return
            }
            // zoom to the extent of the dataset
            if (!selectedDataset.value) {
                console.log('No dataset selected')
                return
            }
            if (!selectedDataset.value.extent) {
                console.log('No extent found for dataset')
                return
            }
            // type check that extent is a string
            if (typeof selectedDataset.value.extent !== 'string') {
                console.log('Extent is not a string')
                return
            }
            var extent: string = selectedDataset.value.extent
            if (!extent) {
                console.log('No extent found for dataset')
                return
            }
            if (!mapStore.map) {
                throw new Error('Map not initialized')
            }
            const wkt = new WKT()
            console.log('Original extent:', extent)
            // openlayers doesn't like the SRID=4326; prefix
            extent = extent.replace(/SRID=4326;\s*/i, '')
            console.log('Modified extent:', extent)
            const extentGeometry = wkt.readGeometry(extent)
            // const polygon = wkt.readGeometry("SRID=4326;POLYGON ((0 1, 0 2, 1 2, 1 1, 0 1))");
            console.log(extentGeometry)
            const transformedExtent = extentGeometry.transform(
                'EPSG:4326',
                mapStore.map.getView().getProjection()
            )

            // OL expects polygon to be:
            // [[WN], [WS], [ES], [EN], [WN]]
            console.log('aloha')
            const olSiteCoords = extentGeometry
                .getCoordinates()[0]
                .map((coord) => fromLonLat(coord))
            const sitePolygon = new Polygon([olSiteCoords])
            const siteFeature = new Feature(sitePolygon)
            // Create a vector source and layer
            const vectorSource = new VectorSource({
                features: [siteFeature]
            })
            const vectorLayer = new VectorLayer({
                source: vectorSource
                // style: polyStyle
            })
            // Add the vector layer to the map
            mapStore.map.addLayer(vectorLayer)
            // mapStore.map.getView().fit(transformedExtent.getExtent(), { duration: 2000 })
            mapStore.map.getView().fit(sitePolygon, { padding: [100, 100, 100, 100] })
        }

        const zoomToExtent = (extent: string) => {
            if (!mapStore.map) {
                throw new Error('Map not initialized')
            }
            const wkt = new WKT()

            // openlayers doesn't like the SRID=4326; prefix
            extent = extent.replace(/SRID=4326;\s*/i, '')
            console.log('Modified extent:', extent)
            const extentGeometry = wkt.readGeometry(extent) as Polygon
            const transformedExtent = extentGeometry.transform(
                'EPSG:4326',
                mapStore.map.getView().getProjection()
            )

            const polyStyle = new Style({
                fill: new Fill({
                    color: 'rgba(255, 255, 255, 0.0)'
                }),
                stroke: new Stroke({
                    color: '#dddddd',
                    width: 3,
                    lineDash: [3, 1],
                    lineCap: 'butt'
                })
            })
            const siteFeature = new Feature(transformedExtent)
            // Create a vector source and layer
            const vectorSource = new VectorSource({
                features: [siteFeature]
            })
            const vectorLayer = new VectorLayer({
                source: vectorSource,
                style: polyStyle
            })
            // Add the vector layer to the map
            mapStore.map.addLayer(vectorLayer)
            
            // Store reference to AOI layer for toggle functionality
            aoiLayer = vectorLayer
            
            mapStore.map.getView().fit(transformedExtent, { padding: [100, 100, 100, 100] })
        }

        const toggleAOI = () => {
            if (!aoiLayer) {
                console.log('No AOI layer found')
                return
            }
            
            aoiLayerVisible.value = !aoiLayerVisible.value
            aoiLayer.setVisible(aoiLayerVisible.value)
            console.log('AOI visibility toggled to:', aoiLayerVisible.value)
        }

        const isLoading = ref(false)
        const totalFeatures = ref(0)
        const loadedFeatures = ref(0)
        let activeScattererLoadId = 0
        let activeScattererAbortController: AbortController | null = null
        const loadingProgress = computed(() => {
            if (totalFeatures.value === 0) return 0
            const result = Math.floor((loadedFeatures.value / totalFeatures.value) * 100)
            console.log(`Progress: ${loadedFeatures.value}/${totalFeatures.value} = ${result}%`)
            return result
        })

        const findFeaturesForDataset = () => {
            if (!selectedDataset.value) {
                console.log('No dataset selected')
                return
            }

            const datasetId = selectedDataset.value.id
            if (datasetId === null || datasetId === undefined) {
                console.log('No dataset id')
                return
            }

            activeScattererLoadId += 1
            const thisLoadId = activeScattererLoadId

            if (activeScattererAbortController) {
                activeScattererAbortController.abort()
            }
            activeScattererAbortController = new AbortController()
            const { signal } = activeScattererAbortController

            layerSetup()
            pointsSource().clear()

            // Reset loading state
            isLoading.value = true
            loadedFeatures.value = 0
            totalFeatures.value = 0

            // Query the API for the feature count of the selected dataset
            fetch(`/api/scatterer-info/?limit=1&dataset=${datasetId}`, { signal })
                .then((response) => {
                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`)
                    }
                    return response.json()
                })
                .then((data) => {
                    if (thisLoadId !== activeScattererLoadId) {
                        return
                    }

                    // Get the total feature count
                    totalFeatures.value = data.count
                    console.log(`Total features for dataset: ${totalFeatures.value}`)
                    if (totalFeatures.value === 0) {
                        console.log('No features found for dataset')
                        isLoading.value = false
                        return
                    }

                    // Function to load features in batches of 10,000 with limited concurrency
                    console.log(`Loading ${totalFeatures.value} features`)
                    const loadFeaturesInChunks = async () => {
                        const chunkOffsets: number[] = []
                        for (let offset = 0; offset < totalFeatures.value; offset += 10000) {
                            chunkOffsets.push(offset)
                        }

                        const concurrencyLimit = Math.max(1, maxConcurrentRequests.value || 1)
                        console.log(
                            `Using up to ${concurrencyLimit} concurrent scatterer requests`
                        )

                        const loadChunk = async (offset: number) => {
                            const batchSize = Math.min(10000, totalFeatures.value - offset)
                            const response = await fetch(
                                `/api/scatterer-info/?limit=${batchSize}&offset=${offset}&dataset=${datasetId}`,
                                { signal }
                            )

                            if (!response.ok) {
                                throw new Error(`HTTP error! status: ${response.status}`)
                            }

                            if (thisLoadId !== activeScattererLoadId) {
                                return
                            }

                            const batchData = await response.json()
                            const data = batchData.data as Record<string, ScattererPoint>
                            const bdVals = Object.values(data)
                            const filtered = bdVals.filter(
                                (point): point is FilteredScattererPoint =>
                                    point[0] !== null &&
                                    point[1] !== null &&
                                    point[2] !== null &&
                                    point[3] !== null &&
                                    point[4] !== null
                            )
                            const filteredByCoh = filtered.filter(
                                (point) => point[2] > cohThreshold.value
                            )
                            addPointsToMap(filteredByCoh)

                            loadedFeatures.value += batchSize
                            console.log(
                                `Loaded ${loadedFeatures.value} of ${totalFeatures.value} features`
                            )
                        }

                        let nextChunkIndex = 0
                        const workers = Array.from(
                            { length: Math.min(concurrencyLimit, chunkOffsets.length) },
                            async () => {
                                while (nextChunkIndex < chunkOffsets.length) {
                                    const currentIndex = nextChunkIndex++
                                    await loadChunk(chunkOffsets[currentIndex])
                                }
                            }
                        )

                        await Promise.all(workers)
                        if (thisLoadId === activeScattererLoadId) {
                            isLoading.value = false
                        }
                    }

                    // Start loading features in chunks
                    loadFeaturesInChunks()
                        .then(() => console.log('All features loaded'))
                        .catch((error) => {
                            if (error instanceof DOMException && error.name === 'AbortError') {
                                return
                            }
                            console.error('Error loading features:', error)
                            if (thisLoadId === activeScattererLoadId) {
                                isLoading.value = false
                            }
                        })
                })
                .catch((error) => {
                    if (error instanceof DOMException && error.name === 'AbortError') {
                        return
                    }
                    console.error('Error fetching feature count:', error)
                    if (thisLoadId === activeScattererLoadId) {
                        isLoading.value = false
                    }
                })
        }

        onMounted(() => {
            const map = mapStore.map
            if (!map) {
                throw new Error('Map not initialized')
            }

            datasetsAdapter.fetchDatasets().then((response) => {
                console.log(datasetsAdapter.datasets.value)
                // zoom to first dataset extent
                if (datasetsAdapter.datasets.value.length > 0) {
                    zoomToExtent(datasetsAdapter.datasets.value[0].extent)
                } else {
                    // zoom to the site extent
                    sitesAdapter.fetchItems().then(() => {
                        const site = sitesAdapter.sites.value.find(
                            (site) => site.name === siteId.value
                        )
                        console.log('Site:', site)
                        // convert the [N, E, S, W] string to a WKT polygon
                        const noBrackets = site.geometry.slice(1, site.geometry.length - 1)
                        const limits = noBrackets.split(',').map((coord) => parseFloat(coord))
                        const wktpolygon = `POLYGON((${limits[1]} ${limits[0]}, ${limits[3]} ${limits[0]}, ${limits[3]} ${limits[2]}, ${limits[1]} ${limits[2]}, ${limits[1]} ${limits[0]}))`
                        if (site) {
                            zoomToExtent(wktpolygon)
                        }
                    })
                }
            })

            const pop_container = document.getElementById('popup')
            const pop_content = document.getElementById('popup-content')
            const pop_closer = document.getElementById('popup-closer')

            // Make sure these are not null
            if (pop_container === null || pop_content === null || pop_closer === null) {
                throw new Error('popup elements are null')
            }

            const pop_overlay = new Overlay({
                element: pop_container
            })
            pop_overlay.setPosition(undefined)

            function close_chart_popup() {
                if (pop_container) {
                    pop_container.style.display = 'none'
                }
                pop_overlay.setPosition(undefined)
                isChartOverlayOpen.value = false
            }

            pop_closer.onclick = function () {
                close_chart_popup()
            }

            // Add an onclick-event to the map
            map.on('click', (event) => {
                if (!selectedDataset.value) {
                    console.log('No dataset selected')
                    return
                }

                // Check if the chart overlay is open
                if (isChartOverlayOpen.value) {
                    console.log('Chart overlay is open')
                    // close it
                    close_chart_popup()
                    return
                }

                // Get the coordinates of the pointer event
                const coords = event.coordinate
                // Y axis label
                // console.log('Setting popup position to: ', coords)
                pop_overlay.setPosition(coords)
                // // deep copy the coords
                // const popup_coords = coords.slice()
                // // get half the height and width
                // const halfHeight = pop_container.clientHeight / 2
                // const halfWidth = pop_container.clientWidth / 2
                // // convert map pixels to coordinates
                // const centreCoords = map.getCoordinateFromPixel([halfWidth, halfHeight])
                // const originCoords = map.getCoordinateFromPixel([0, 0])
                // const halfWidthInCoords = centreCoords[0] - originCoords[0]
                // const halfHeightInCoords = centreCoords[1] - originCoords[1]
                // console.log('Half width in coords: ', halfWidthInCoords)
                // console.log('Half height in coords: ', halfHeightInCoords)
                // pop_overlay.setPosition([popup_coords[0] - halfWidthInCoords, popup_coords[1] - halfHeightInCoords])
                // console.log('Setting popup position to: ', [popup_coords[0] - halfWidthInCoords, popup_coords[1] - halfHeightInCoords])
                // console.log('Popup overlay position set to: ', pop_overlay.getPosition())

                // // set a watch for 5seconds
                // setTimeout(() => {
                //   const popPosAfter5 = pop_overlay.getPosition()
                //   if (popPosAfter5) {
                //     console.log('Popup overlay is still open after 5 seconds:', popPosAfter5)
                //     pop_overlay.setPosition(popPosAfter5)
                //     // get apparent size of overlay
                //     const apparentSize = pop_overlay.getElement().getBoundingClientRect()
                //     console.log('Apparent size of overlay:', apparentSize)
                //     // set positioning centre centre
                //     pop_overlay.setPositioning('center-center')
                //   }
                // }, 3000)

                // translate the pop_overlay by -100% of height
                const heightOfPopup = pop_container.clientHeight
                const widthOfPopup = pop_container.clientWidth
                console.log('Width of popup:', widthOfPopup)
                // Set the width of the popup as a proportion of the map width
                updatePopupWidth(map.getSize()[0])

                // shift slightly to the left to account for the popup arrow position,
                // and shift up by the height of the arrow
                // pop_overlay.setOffset([-48, -heightOfPopup - 10])
                // pop_overlay.setOffset([-48, -heightOfPopup - 10])

                const coordsFormat1 = coords.map((coord) => coord.toFixed(0)).join(', ')

                // coordinates style - seperate little grey box
                const coordsStyle =
                    'background-color: rgba(200, 200, 200, 0.5); padding: 5px; border-radius: 5px;'
                // const addStyle => (message: string) => {
                //   return `<span style="${coordsStyle}">${message}</span>`
                // }
                // Add the coordinates to the popup content
                // pop_content.innerHTML = `<p>Coordinates: ${coordsFormat2}</p>`
                // With style:

                pop_content.innerHTML = `<h3>Time Series - ${selectedDataset.value.name}</h3>`

                pop_content.innerHTML += `<p>Map Coordinates (EPSG:3857): <span style="${coordsStyle}">[${coordsFormat1}] </span></p>`
                // Add the coordinates in lat/lon
                const coordsFormat2 = toLonLat(coords)
                    .map((coord) => coord.toFixed(4))
                    .join(', ')
                // Flip xy so it's lat, lon
                const lat = coordsFormat2.split(',')[1]
                const lon = coordsFormat2.split(',')[0]
                pop_content.innerHTML += `<p>Geographic Coordinates (EPSG:4326): <span style="${coordsStyle}">[${lat}, ${lon}]</span></p>`
                // Add displacement rate
                pop_content.innerHTML += `<p>Model displacement rate: <span style="${coordsStyle}"> LOADING_VELOCITY mm/year</span></p>`
                pop_content.innerHTML += `<p>Coherence: <span style="${coordsStyle}">LOADING_COHERENCE</span></p>`
                pop_content.innerHTML += '<canvas id="time-series-chart"></canvas>'
                pop_content.innerHTML +=
                    '<button id="copy-timeseries-btn" style="margin-top: 10px; padding: 5px 10px; background-color: #007bff; color: white; border: none; border-radius: 3px; cursor: pointer;">Copy Data to Clipboard</button>'
                // Add some dummy dates and values for the chart
                var dates = []
                var values = []

                // set the popup container display to inherit
                pop_container.style.display = 'inherit'

                // get dates from the api
                fetch(`/api/psi-datasets/${selectedDataset.value.id}/`)
                    .then((response) => {
                        if (!response.ok) {
                            throw new Error(`HTTP error! status: ${response.status}`)
                        }
                        return response.json()
                    })
                    .then((data) => {
                        console.log('data.date_series:', data.date_series)

                        dates = data.date_series
                        // Replace 'LOADING' with the actual values
                    })

                // get the nearest scatterer to the clicked point
                fetch(
                    `/api/nearest-scatterer/?dataset=${selectedDataset.value.id}&lat=${lat}&lon=${lon}`
                )
                    .then((response) => {
                        if (!response.ok) {
                            throw new Error(`HTTP error! status: ${response.status}`)
                        }
                        console.log(response)
                        return response.json()
                    })
                    .then((nearestScatterer) => {
                        console.log(nearestScatterer)
                        // Use the id to get the time series data
                        const scattererId = nearestScatterer.time_series
                        fetch(
                            `/api/psi-scatterer-timeseries/?dataset=${selectedDataset.value.id}&id=${scattererId}`
                        )
                            .then((response) => {
                                if (!response.ok) {
                                    throw new Error(`HTTP error! status: ${response.status}`)
                                }
                                return response.json()
                            })
                            .then((dataJson) => {
                                console.log('dataJson.values:', dataJson.values)
                                console.log('SELECTED POINT')
                                console.log('nearestScatterer:', nearestScatterer)
                                selectedPoint.value = nearestScatterer
                                var vel_3_sig_fig = (nearestScatterer.velocity * 1000).toFixed(2)
                                pop_content.innerHTML = pop_content.innerHTML.replace(
                                    'LOADING_VELOCITY',
                                    vel_3_sig_fig
                                )
                                var coh_3_sig_fig = nearestScatterer.coherence.toFixed(3)
                                pop_content.innerHTML = pop_content.innerHTML.replace(
                                    'LOADING_COHERENCE',
                                    coh_3_sig_fig
                                )

                                selectedPoint.value = nearestScatterer
                                // Create a time series chart
                                const formattedData = dates.reduce((acc, date, index) => {
                                    acc[date] = dataJson.values[index]
                                    return acc
                                }, {})

                                // Convert this into a chart.js time series chart
                                // make some data with 'labels', 'datasets'
                                const labels = Object.keys(formattedData)
                                const data = {
                                    labels: labels,
                                    datasets: [
                                        {
                                            label: 'Displacement Time Series',
                                            data: Object.values(formattedData),
                                            fill: false,
                                            borderColor: 'rgb(75, 192, 192)',
                                            tension: 0.1
                                        }
                                    ]
                                }
                                // get the canvas
                                const chartbox = document.getElementById(
                                    'time-series-chart'
                                ) as HTMLCanvasElement
                                if (chartbox === null) {
                                    throw new Error('chartbox is null')
                                }

                                // y axis label
                                const yaxis = document.createElement('div')
                                yaxis.innerHTML = 'Displacement (m)'
                                yaxis.style.position = 'absolute'
                                yaxis.style.top = '10px'
                                yaxis.style.left = '10px'

                                // To control the size of the chart we need to set the width of the canvas / parent div
                                console.log('chartbox:', chartbox)
                                console.log('chartbox.style.width:', chartbox.style.width)
                                console.log('baseWidth.value:', baseWidth.value)
                                // set the canvas width
                                console.log('Setting chart width to: ', baseWidth.value)
                                chartbox.innerHTML = ' '
                                chartbox.width = baseWidth.value
                                isChartOverlayOpen.value = true

                                // Get the canvas
                                new Chart(chartbox, {
                                    type: 'line',
                                    data: data,
                                    options: {
                                        responsive: true,
                                        scales: {
                                            y: {
                                                title: {
                                                    // Add this section
                                                    display: true, // Make sure the title is displayed
                                                    text: 'Displacement (m)', // Set the title text
                                                    color: 'black', // Optional: Set the color of the title
                                                    font: {
                                                        // Optional: Customize font properties
                                                        size: 16, // Font size
                                                        family: 'Arial' // Font family
                                                    }
                                                }
                                            }
                                        }
                                    }
                                })

                                // Set position to middle of map container
                                const middleOfMapCoords = map.getCoordinateFromPixel([
                                    map.getSize()[0] / 2,
                                    map.getSize()[1] / 2
                                ])
                                pop_overlay.setPosition(middleOfMapCoords)
                                // Reset the positioning now content is loaded
                                pop_overlay.setPositioning('center-center')
                                // Set size
                                updatePopupWidth(map.getSize()[0])
                                // check if the pop_container has style
                                if (!pop_container.style.background) {
                                    pop_container.style.backgroundColor = 'beige'
                                }
                                // check the class, should contain 'ol-popup'
                                if (!pop_container.classList.contains('ol-popup')) {
                                    pop_container.classList.add('ol-popup')
                                    console.error('ol-popup class added to popup container')
                                } else {
                                    console.error(
                                        'ol-popup class already exists on popup container'
                                    )
                                }

                                // Add event listener for copy button
                                const copyBtn = document.getElementById('copy-timeseries-btn')
                                if (copyBtn) {
                                    copyBtn.addEventListener('click', () => {
                                        const latNum = parseFloat(lat)
                                        const lonNum = parseFloat(lon)
                                        const valuesArray = Object.values(formattedData) as number[]
                                        copyTimeSeriesData(dates, valuesArray, latNum, lonNum)
                                    })
                                }
                            })
                            .catch((error) =>
                                console.error('Error fetching time series data:', error)
                            )
                    })
                    .catch((error) => console.error('Error fetching nearest scatterer:', error))
            })
            // Add the overlay to the map
            map.addOverlay(pop_overlay)

            // Add a vue watch for cohThreshold
            watch(cohThreshold, (newVal) => {
                console.log('Coh threshold changed:', newVal)
                if (newVal !== initialControlValues.value.cohThreshold) {
                    controlsChanged.value = true
                }
            }) // watch

            // Add a vue watch for pointSize
            watch(pointSize, (newVal) => {
                console.log('Point size changed:', newVal)
                if (newVal !== initialControlValues.value.pointSize) {
                    controlsChanged.value = true
                }
            }) // watch
            
            // Add a vue watch for velThreshold
            watch(velThreshold, (newVal) => {
                console.log('Velocity threshold changed:', newVal)
                if (newVal !== initialControlValues.value.velThreshold) {
                    controlsChanged.value = true
                }
            }) // watch

            // Add a vue watch for maxConcurrentRequests
            watch(maxConcurrentRequests, (newVal) => {
                console.log('Max concurrent requests changed:', newVal)
                if (newVal !== initialControlValues.value.maxConcurrentRequests) {
                    controlsChanged.value = true
                }
            }) // watch
            
            // Add a vue watch for selectedCategory
            watch(selectedCategory, (newVal) => {
                console.log('Selected category changed:', newVal)
                if (newVal !== initialControlValues.value.selectedCategory) {
                    controlsChanged.value = true
                }
            }) // watch
        }) // onMounted

        // Computed property for formatted velocity threshold display
        const formattedVelThreshold = computed(() => {
            return (velThreshold.value * 1000).toFixed(1)
        })

        return {
            siteId,
            datasetsAdapter,
            cohThreshold,
            pointSize,
            velThreshold,
            maxConcurrentRequests,
            formattedVelThreshold,
            handleDatasetChange,
            findFeaturesForDataset,
            selectedPoint,
            drawer,
            selectedCategory,
            categories,
            set_preset,
            isLoading,
            loadingProgress,
            totalFeatures,
            copyTimeSeriesData,
            goBackToSite,
            toggleAOI,
            aoiLayerVisible,
            controlsChanged,
            reloadWithNewControls
        }
    }
})
</script>

<template>
    <div class="flex">
        <div class="w-1over3">
            <div class="flex items-center gap-3 mb-2">
                <Button
                    @click="goBackToSite"
                    icon="pi pi-arrow-left"
                    severity="secondary"
                    outlined
                    size="small"
                    class="flex-shrink-0"
                    aria-label="Back to Site"
                ></Button>
                <h1 class="m-0">{{ siteId }} PSI Datasets</h1>
            </div>
            <!-- Table for available point layers -->
            <Fieldset legend="Deformation Datasets" :toggleable="true" :collapsed="false">
                <table>
                    <thead>
                        <tr>
                            <th>Available Datasets</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(dataset, index) in datasetsAdapter.datasets.value" :key="index">
                            <td v-if="dataset.id !== null">
                                <input
                                    type="radio"
                                    :id="String(dataset.id)"
                                    name="pointLayer"
                                    :value="dataset.name"
                                    @change="handleDatasetChange(dataset)"
                                />
                                <label :for="dataset.name">{{ dataset.displayName || dataset.name }}</label>
                            </td>
                            <td v-else>Error with dataset id</td>
                        </tr>
                    </tbody>
                </table>
                <!-- Load button -->
                <Button @click="findFeaturesForDataset" :disabled="isLoading">Load</Button>

                <!-- Loading progress bar -->
                <div v-if="isLoading" class="mt-4">
                    <ProgressBar
                        :value="loadingProgress"
                        :showValue="true"
                        class="custom-progress"
                        style="width: 100%"
                    />
                    <div class="progress-text">
                        Loading points: {{ Math.round(loadingProgress) }}%
                    </div>
                </div>
            </Fieldset>

            <Fieldset legend="View Controls" :toggleable="true" class="view-controls-container">
                <!-- Reload button for when controls change -->
                <Button 
                    v-if="controlsChanged"
                    @click="reloadWithNewControls"
                    :disabled="isLoading"
                    class="reload-button"
                    severity="warning"
                    size="small"
                    icon="pi pi-refresh"
                    v-tooltip="'Reload to see changes'"
                >
                    RELOAD
                </Button>
                
                <h3>Use Case Presets:</h3>
                <div class="card">
                    <div class="presets-container">
                        <div
                            v-for="category in categories"
                            :key="category.key"
                            class="preset-option"
                        >
                            <RadioButton
                                v-model="selectedCategory"
                                :id="category.key"
                                :value="category.key"
                                @change="
                                    () => {
                                        set_preset()
                                    }
                                "
                            />
                            <!-- <input type="radio" :id="category.key" name="category" :value="category.key" /> -->
                            <label :for="category.key">{{ category.name }}</label>
                        </div>
                    </div>
                </div>
                <!-- Table for view controls -->
                <h3>Controls</h3>
                <table class="controls-table">
                    <!-- <tr>
            <th>Control</th>
            <th>Value</th>
          </tr> -->
                    <!-- Dropdown for colour ramp preset -->
                    <tbody>
                    <tr>
                        <td>
                            Colour Ramp<br />
                            <Button
                                @click="
                                    drawer.openDrawer('Colour Ramp', [
                                        'This controls the color mapping of the point cloud data'
                                    ])
                                "
                                class="help-button"
                                >tips</Button
                            >
                        </td>
                        <td>
                            <select id="colour-ramp" name="colour-ramp">
                                <option value="RGB">RGB</option>
                            </select>
                        </td>
                    </tr>
                    <tr>
                        <td>
                            Point Size<br />
                            <Button
                                @click="
                                    drawer.openDrawer('Point Size', [
                                        'This is the size of the points rendered on the map'
                                    ])
                                "
                                class="help-button"
                                >tips</Button
                            >
                        </td>
                        <td>
                            <input
                                type="range"
                                id="point-size"
                                name="point-size"
                                min="1"
                                max="10"
                                step="0.1"
                                value="3"
                                v-model.number="pointSize"
                            /><br />
                            <span class="slider-label">{{ pointSize }} pixels</span>
                        </td>
                    </tr>
                    <tr>
                        <td>
                            Coherence Threshold<br />
                            <Button
                                @click="
                                    drawer.openDrawer('Coherence Threshold', [
                                        `Coherence in InSAR (Interferometric Synthetic Aperture Radar) data processing is a measure of how similar the radar signals are between two different observations of the same area. It
        ranges from 0 (completely different signals) to 1 (perfectly identical signals).`,
                                        `
        For deformation monitoring, coherence tells us how reliable the phase difference
        (interferogram) is for measuring ground displacement. High coherence means that the surface
        properties haven t changed much between radar acquisitions, so we can confidently extract
        deformation information. Low coherence, on the other hand, indicates changes due to
        vegetation growth, land use changes, or atmospheric noise, making deformation measurements
        less reliable.`
                                    ])
                                "
                                class="help-button"
                                >tips</Button
                            >
                        </td>
                        <td>
                            <input
                                type="range"
                                id="coherence-threshold"
                                name="coherence-threshold"
                                min="0"
                                max="1"
                                step="0.01"
                                value="0.7"
                                v-model="cohThreshold"
                            /><br />

                            <span class="slider-label">{{ cohThreshold }}</span>
                        </td>
                    </tr>
                    <tr>
                        <td>
                            Velocity Colour Limit<br />
                            <Button
                                @click="
                                    drawer.openDrawer('Velocity Colour Limit', [
                                        `
              The velocity colour limit is measured in millimeters and controls the colour scaling of points being rendered on the map.
              For example a low value (e.g. 1mm) will show any displacement rates above this value as the colour on the extreme end of the colour scale.
              The limit is 'one-sided' so the colour scale will be symmetric around zero - a colour limit of 1mm will show displacements from -1mm to 1mm.
              `
                                    ])
                                "
                                class="help-button"
                                >tips</Button
                            >
                        </td>
                        <td>
                            <input
                                type="range"
                                id="velocity-ramp"
                                name="velocity-ramp"
                                min="0"
                                max="0.01"
                                step="0.0001"
                                v-model="velThreshold"
                            /><br />

                            <span class="slider-label">{{ formattedVelThreshold }} mm/year</span>
                        </td>
                    </tr>
                    <tr>
                        <td>
                            Request Concurrency<br />
                            <Button
                                @click="
                                    drawer.openDrawer('Request Concurrency', [
                                        'Limits how many scatterer data requests can be in flight at once.',
                                        'Lower values reduce load on the API server but may increase total load time.',
                                        'A value around 10 is a sensible default for avoiding request spikes.'
                                    ])
                                "
                                class="help-button"
                                >tips</Button
                            >
                        </td>
                        <td>
                            <input
                                type="range"
                                id="request-concurrency"
                                name="request-concurrency"
                                min="1"
                                max="20"
                                step="1"
                                v-model.number="maxConcurrentRequests"
                            /><br />
                            <span class="slider-label">{{ maxConcurrentRequests }} requests</span>
                        </td>
                    </tr>
                    <tr>
                        <td>
                            Date Range<br />
                            <Button
                                @click="
                                    drawer.openDrawer('Date Range', [
                                        'This feature allows filtering of time series data by date range.',
                                        'Currently disabled - this functionality will be implemented in an upcoming release.',
                                        'When active, you will be able to select start and end dates to filter the PSI data displayed on the map.'
                                    ])
                                "
                                class="help-button"
                                >tips</Button
                            >
                        </td>
                        <td v-tooltip="'This feature is currently disabled and will be implemented in an upcoming release. When active, you will be able to select start and end dates to filter the PSI data displayed on the map.'">
                            <div 
                                class="date-range-container"
                            >
                                <input type="date" id="date-range" name="date-range" disabled />
                                <br />to<br />
                                <input type="date" id="date-range" name="date-range" disabled />
                            </div>
                        </td>
                    </tr>
                    </tbody>
                </table>
            </Fieldset>
        </div>
        <div class="w-2over3">
            <MapComponent>
                <template #customControls>
                    <Button @click="toggleAOI">
                        {{ aoiLayerVisible ? 'Hide AOI' : 'Show AOI' }}
                    </Button>
                </template>
            </MapComponent>
        </div>
    </div>
    <!-- container -->
    <div id="popup" class="ol-popup">
        <a href="#" id="popup-closer" class="ol-popup-closer"></a>
        <div id="popup-content"></div>
    </div>
</template>

<style scoped>
.flex {
    display: flex;
}

.w-1over3 {
    width: 33.33%;
}

.w-2over3 {
    width: 66.67%;
}

#false-color-composite-list {
    display: none;
}

#page-header {
    text-align: center;
    color: var(--color-text);
    font-size: 24px;
}

/* Add a global class that forces the grid layour for container */
.site-hub-container {
    display: grid;
    grid-template-columns: 1fr 3fr;
    /* Two columns, adjust the width as needed */
}

/* Controls on the left */
.controls {
    /* background-color: #f0f0f0;  */
    /* Background color for the left column */
    padding: 20px;
    /* Add padding for spacing */
}

.control-box {
    border: var(--color-text) 1px double;
    padding: 10px;
}

/* Map container on the right */
.map-container {
    /* You can adjust the size of the map container here */
    width: 100%;
    height: 400px;
    /* Adjust the height as needed */
    position: relative;
    /* Ensure relative positioning for children elements */
}

/* Style map buttons as needed */
.map-container button {
    margin-top: 10px;
    /* Add margin between buttons and the map */
}

/* Style the links in the control panel as buttons */
.vertical-controls a {
    display: flex;
    flex-direction: column;
    padding: 10px;
    margin: 5px;
    border: 1px solid var(--color-border);
    border-radius: 5px;
    text-decoration: none;
    color: var(--color-text);
    background-color: var(--color-background);
}

.vertical-controls a {
    flex: 1;
    text-align: center;
}

/* Hover background */
.vertical-controls a:hover {
    background-color: var(--color-background-mute);
}

.horizontal-controls {
    display: flex;
    flex-direction: row;
    padding: 10px;
    justify-content: space-evenly;
}

.no-border {
    border: none;
}

.site-data-item {
    background-color: var(--color-background-mute);
    padding: 5px;
    margin: 5px;
    border: 1px solid var(--brand-color);
    border-radius: 5px;
    text-decoration: none;
    color: #000;
}

.site-data-item:hover {
    background-color: var(--color-background);
}

.ol-overlay-container {
    background: beige;
}

.ol-popup {
    background: azure !important;
    padding: 12px;
    border-radius: 10px;
}

.ol-popup-closer {
    text-decoration: none;
    position: absolute;
    top: 2px;
    right: 8px;
}

.ol-popup-closer:after {
    content: '✖';
}

.custom-progress :deep(.p-progressbar) {
    height: 1.5rem;
    background: #f0f0f0;
}

.custom-progress :deep(.p-progressbar-value) {
    background: #4caf50;
    transition: width 0.2s ease;
}

.custom-progress :deep(.p-progressbar-label) {
    color: #000;
    font-weight: bold;
}

.progress-text {
    text-align: center;
    font-size: 0.875rem;
    margin-top: 0.25rem;
    font-weight: 600;
}

.help-button {
    margin-right: 12px;
    font-size: 0.75rem;
    padding: 2px 8px;
    min-width: 16px;
    height: 16px;
    text-transform: lowercase;
}

.slider-label {
    display: inline-block;
    min-width: 3rem;
    text-align: left;
    font-family: 'Courier New', monospace;
    font-weight: 500;
    margin-left: 8px;
    background: ghostwhite;
    border: 1px solid gainsboro;
    border-radius: 4px;
    padding: 2px 6px;
}

/* Container query for responsive controls */
.view-controls-container {
    container-type: inline-size;
    position: relative;
}

.view-controls-container h3 {
    font-size: 1rem;
    font-weight: 600;
    margin: 0.5rem 0;
}

.reload-button {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    z-index: 10;
    font-size: 0.75rem;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
}

.presets-container {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    justify-content: flex-start;
}

.preset-option {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex: 1 1 auto;
    min-width: 0; /* Allow shrinking */
}

.preset-option label {
    white-space: normal;
    word-wrap: break-word;
    line-height: 1.2;
    font-size: 0.9rem;
}

/* Make presets stack vertically on narrow containers */
@container (max-width: 500px) {
    .presets-container {
        flex-direction: column;
        gap: 0.5rem;
    }
    
    .preset-option {
        width: 100%;
        justify-content: flex-start;
    }
}

.controls-table {
    width: 100%;
    table-layout: fixed;
}

.controls-table td {
    padding: 8px 4px;
    vertical-align: top;
}

.controls-table td:first-child {
    width: 40%;
}

.controls-table td:last-child {
    width: 60%;
}

/* Switch to stacked layout for narrow containers */
@container (max-width: 400px) {
    .controls-table,
    .controls-table tbody,
    .controls-table tr,
    .controls-table td {
        display: block;
        width: 100%;
    }
    
    .controls-table tr {
        border-bottom: 1px solid #e0e0e0;
        margin-bottom: 1rem;
        padding-bottom: 1rem;
    }
    
    .controls-table td {
        padding: 4px 0;
        border: none;
    }
    
    .controls-table td:first-child {
        font-weight: 600;
        margin-bottom: 0.5rem;
    }
    
    .controls-table input[type="range"] {
        width: 100%;
        max-width: 100%;
    }
    
    .controls-table select {
        width: 100%;
        max-width: 100%;
    }
}
</style>
